import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    CircularProgress
} from '@material-ui/core';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useUploadFile } from '../../hooks/useUploadFile';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1)
    },
    inputWrapper: {
        display: 'flex',
        gap: 0,
        alignItems: 'stretch',
        '& .MuiTextField-root': {
            flex: 1
        }
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            '& fieldset': {
                borderRight: 'none'
            }
        }
    },
    uploadButton: {
        minWidth: 120,
        height: 56,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeft: 'none',
        backgroundColor: '#ff9800',
        color: '#fff',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#e68900',
            boxShadow: 'none'
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[300],
            color: theme.palette.grey[500]
        }
    },
    clearButton: {
        height: 56,
        minWidth: 48,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        color: theme.palette.error.main,
        borderLeft: `1px solid ${theme.palette.divider}`,
        '&:hover': {
            backgroundColor: theme.palette.error.light,
            color: '#fff'
        }
    },
    input: {
        display: 'none'
    },
    imagePreview: {
        maxWidth: '100%',
        maxHeight: 200,
        marginTop: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        display: 'block'
    },
    uploadProgress: {
        marginTop: theme.spacing(1)
    },
    progressText: {
        fontSize: '0.75rem',
        color: '#ff9800',
        fontWeight: 500
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(0.5),
        fontSize: '0.75rem'
    }
}));

const ImageUpload = ({ value, onChange, label = "Image URL", showClearButton = true, uploadPath = 'gams-common/challengeImages', acceptedFileTypes = 'image/*', uniqueId }) => {
    const classes = useStyles();
    const [error, setError] = useState('');
    const uploadFile = useUploadFile();
    const fileInputRef = React.useRef(null);
    const inputId = uniqueId
        ? `image-upload-${uniqueId}`
        : `image-upload-button-${label.replace(/[^a-zA-Z0-9]/g, '-')}`;

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const acceptedTypes = acceptedFileTypes.split(',').map(t => t.trim());
        const isValid = acceptedTypes.some(type => {
            if (type.endsWith('/*')) {
                return file.type.startsWith(type.replace('/*', '/'));
            }
            return file.type === type;
        });
        
        if (!isValid) {
            setError(`Please select a valid file (${acceptedFileTypes})`);
            event.target.value = null;
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            event.target.value = null;
            return;
        }

        setError('');

        try {
            const result = await uploadFile.mutateAsync({
                file,
                path: uploadPath,
                startWith: 'challenge_'
            });

            // Extract URL from response - restClient unwraps response.data
            const imageUrl = result.uploadPhoto?.url || result.data?.uploadPhoto?.url || result.url;
            if (imageUrl) {
                onChange({ target: { value: imageUrl } });
                // Clear file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            } else {
                setError('No URL returned from upload');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message || 'Upload failed. Please try again.');
        }
    };

    const handleClearImage = () => {
        onChange({ target: { value: '' } });
        setError('');
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.inputWrapper}>
                <TextField
                    label={label}
                    value={value || ''}
                    onChange={onChange}
                    variant="outlined"
                    placeholder="Enter image URL or upload an image"
                    disabled={uploadFile.isLoading}
                    className={classes.textField}
                />
                <input
                    accept={acceptedFileTypes}
                    className={classes.input}
                    id={inputId}
                    type="file"
                    onChange={handleFileSelect}
                    disabled={uploadFile.isLoading}
                    ref={fileInputRef}
                />
                <label htmlFor={inputId} style={{ display: 'flex' }}>
                    <Button
                        variant="contained"
                        component="span"
                        startIcon={uploadFile.isLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                        disabled={uploadFile.isLoading}
                        className={classes.uploadButton}
                    >
                        {uploadFile.isLoading ? 'Uploading' : 'Upload'}
                    </Button>
                </label>
                {showClearButton && value && !uploadFile.isLoading && (
                    <Button
                        onClick={handleClearImage}
                        className={classes.clearButton}
                        title="Clear image"
                    >
                        <DeleteIcon />
                    </Button>
                )}
            </Box>

            {uploadFile.isLoading && (
                <Box className={classes.uploadProgress}>
                    <Typography className={classes.progressText}>
                        Uploading...
                    </Typography>
                </Box>
            )}

            {error && (
                <Typography className={classes.errorText}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default ImageUpload;
